import { useState, useCallback, useMemo } from 'react';
import { useContactAttributesConfig } from './useContactAttributesConfig';
import { CustomAttributeType } from '../types';
import { FieldType } from '../../../hooks/useSchema';

// --- Types & Constants ---
export const MAPPABLE_FIELDS = [
  { value: 'first_name', label: 'Имя' },
  { value: 'last_name', label: 'Фамилия' },
  { value: 'middle_name', label: 'Отчество' },
  
  // Combined name options
  { value: '_combined_name_first_last', label: 'Имя + Фамилия' },
  { value: '_combined_name_last_first', label: 'Фамилия + Имя' },
  { value: '_combined_name_fio', label: 'ФИО (Фамилия Имя Отчество)' },

  { value: 'gender', label: 'Пол' },
  { value: 'birth_date', label: 'Дата рождения' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Телефон' },
  { value: 'description', label: 'Описание' },
  { value: 'organization_name', label: 'Компания' },
  { value: 'organization_position', label: 'Должность' },
  
  // Tags mapping
  { value: 'tag_names', label: 'Теги (через запятую/точку с запятой)' },

  // Geo mapping
  { value: 'geo_country', label: 'Страны (через запятую/точку с запятой)' },
  { value: 'geo_city', label: 'Города (через запятую/точку с запятой)' },

  // Consents & Languages
  { value: 'consent_pd', label: 'Согласие на ПД' },
  { value: 'consent_emails', label: 'Согласие на рассылки' },
  { value: 'communication_language', label: 'Языки коммуникации (через запятую)' },
];

const splitBySeparators = (str: string): string[] => {
  if (!str) return [];
  return str.split(/[,;]/)
    .map(s => s.trim())
    .filter(s => s.length > 0);
};

// Helper to safely extract string values, especially from Excel hyperlink objects
const extractTextValue = (val: any): string => {
  if (val === null || val === undefined) return "";
  
  if (typeof val === 'object' && val !== null) {
    if ('text' in val) {
      return String(val.text).trim();
    }
    return ""; 
  }
  
  return String(val).trim();
};

export const useContactsImportFieldsMapping = () => {
  const [mapping, setMapping] = useState<Record<string, string>>({});
  
  // 1. Fetch custom attributes and schema
  const { attributes: customAttributes, schema: customSchema } = useContactAttributesConfig();

  // 2. Dynamically combine standard fields with custom attributes
  const mappableFields = useMemo(() => {
    const customFields = customAttributes.map(attr => ({
      value: attr.code,
      label: `${attr.label} (Доп. атрибут)`, 
    }));
    return [...MAPPABLE_FIELDS, ...customFields];
  }, [customAttributes]);

  const updateMapping = useCallback((header: string, field: string | null) => {
    setMapping(prev => {
      const newMapping = { ...prev };
      if (field) {
        newMapping[header] = field;
      } else {
        delete newMapping[header];
      }
      return newMapping;
    });
  }, []);

  const resetMapping = useCallback(() => {
    setMapping({});
  }, []);

  // Pre-processes a raw Excel row into a backend-ready payload
  const applyMapping = useCallback((row: any) => {
    const payload: any = {
      attributes: {} // Initialize container for custom JSONB attributes
    };
    
    Object.entries(mapping).forEach(([excelHeader, contactField]) => {
      let rawValue = row[excelHeader];
      
      if (rawValue !== undefined && rawValue !== null && rawValue !== "") {
        let value = extractTextValue(rawValue);
        if (!value) return; 

        // --- 0. Handle Custom Attributes ---
        const customFieldConfig = customSchema[contactField];
        if (customFieldConfig) {
          if (customFieldConfig.type === 'bool') {
            const lowerVal = value.toLowerCase();
            const positiveValues = ['yes', 'y', 'true', 't', '1', 'granted', 'да', 'д'];
            payload.attributes[contactField] = positiveValues.includes(lowerVal);
          } else if(customFieldConfig.type === "select") {
            const attr = customAttributes.find(a => a.code === contactField);
            const v = attr?.select_options?.find(o => o.label === (value || "").trim())?.value;
            // Overwrite or ignore multiple selects; keeping simplest approach
            if(v) {
              payload.attributes[contactField] = v;
            }
          } else if(customFieldConfig.type === CustomAttributeType.suggest_multi) {
            const v = value?.trim();
            if(v) {
              const newArr = v.split(/[,;]/).map(s => s.trim()).filter(s => !!s);
              if (newArr.length > 0) {
                payload.attributes[contactField] = [...(payload.attributes[contactField] || []), ...newArr];
              }
            }
          } else {
            // Write text / text-long fields
            // FieldType.textlong -> concatenate with \n
            // FieldType.text (or default) -> concatenate with ;
            const separator = customFieldConfig.type === FieldType.textlong ? '\n' : '; ';
            if (payload.attributes[contactField]) {
              payload.attributes[contactField] += `${separator}${value}`;
            } else {
              payload.attributes[contactField] = value;
            }
          }
          return; // Skip standard field processing
        }
        
        // --- 1. Handle Combined Name Fields ---
        if (contactField.startsWith('_combined_name')) {
          const parts = value.split(/\s+/).filter(Boolean);
          
          if (contactField === '_combined_name_first_last') {
            payload.first_name = parts[0] || '';
            if (parts.length > 1) payload.last_name = parts.slice(1).join(' ');
          } 
          else if (contactField === '_combined_name_last_first') {
            payload.last_name = parts[0] || '';
            if (parts.length > 1) payload.first_name = parts.slice(1).join(' ');
          } 
          else if (contactField === '_combined_name_fio') {
            payload.last_name = parts[0] || '';
            if (parts.length > 1) payload.first_name = parts[1];
            if (parts.length > 2) payload.middle_name = parts.slice(2).join(' ');
          }
        }
        
        // --- 2. Handle Tags (Comma/Semicolon separated) ---
        else if (contactField === 'tag_names') {
          const newTags = splitBySeparators(value);
          if (newTags.length > 0) {
            payload.tag_names = [...(payload.tag_names || []), ...newTags];
          }
        }

        // --- 3. Handle Geo fields (Comma/Semicolon separated) ---
        else if (contactField === 'geo_country' || contactField === 'geo_city') {
          const newGeos = splitBySeparators(value);
          if (newGeos.length > 0) {
            payload[contactField] = [...(payload[contactField] || []), ...newGeos];
          }
        }
        
        // --- 4. Handle Proxy Emails and Phones (Comma/Semicolon separated) ---
        else if (contactField === 'email') {
          const emails = splitBySeparators(value);
          if (emails.length > 0) {
            const emailObjs = emails.map(e => ({ value: e }));
            payload.emails = [...(payload.emails || []), ...emailObjs];
          }
        } 
        else if (contactField === 'phone') {
          const phones = splitBySeparators(value);
          if (phones.length > 0) {
            const phoneObjs = phones.map(p => ({ value: p }));
            payload.phones = [...(payload.phones || []), ...phoneObjs];
          }
        }

        // --- 5. Handle Consents (Boolean/Yes values) ---
        else if (contactField === 'consent_pd' || contactField === 'consent_emails') {
          const lowerVal = value.toLowerCase();
          const positiveValues = ['yes', 'y', 'true', 't', '1', 'granted', 'да', 'д'];
          if (positiveValues.includes(lowerVal)) {
            payload[contactField] = 'yes';
          }
        }

        // --- 6. Handle Communication Languages ---
        else if (contactField === 'communication_language') {
          const langs = splitBySeparators(value).map(l => l.toLowerCase());
          const mappedLangs = new Set<string>(payload[contactField] || []); 
          
          const enMatchers = ['en', 'eng', 'english', 'английский', 'англ'];
          const ruMatchers = ['ru', 'russian', 'русский', 'rus', 'ру', 'рус'];

          langs.forEach(lang => {
            if (enMatchers.includes(lang)) mappedLangs.add('en');
            if (ruMatchers.includes(lang)) mappedLangs.add('ru');
          });

          if (mappedLangs.size > 0) {
            payload[contactField] = Array.from(mappedLangs);
          }
        }
        
        // --- 7. Handle Standard Description (Concatenate with \n) ---
        else if (contactField === 'description') {
           if (payload[contactField]) {
             payload[contactField] += `\n${value}`;
           } else {
             payload[contactField] = value;
           }
        }

        // --- 8. Handle Standard Fields (Overwrite, e.g. first_name) ---
        else {
          payload[contactField] = value;
        }
      }
    });

    // Cleanup empty attributes object if no custom fields were mapped
    if (Object.keys(payload.attributes).length === 0) {
      delete payload.attributes;
    }

    return payload;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapping, customSchema]);

  return {
    mappableFields,
    mapping,
    updateMapping,
    resetMapping,
    applyMapping,
  };
};